// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Button,
  ProgressBar,
  Tag,
  Loading,
  InlineNotification,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from '@carbon/react';
import { Play, Bookmark, Time, CheckmarkFilled } from '@carbon/react/icons';
import { useAuthStore } from '../../store/authStore';

interface LearningSession {
  id: string;
  courseTitle: string;
  lessonTitle: string;
  progress: number;
  lastAccessed: string;
  estimatedTimeRemaining: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  category: string;
  nextLessonTitle?: string;
}

interface RecentActivity {
  id: string;
  type: 'lesson_completed' | 'quiz_passed' | 'assignment_submitted' | 'badge_earned';
  title: string;
  description: string;
  timestamp: string;
  points?: number;
}

const LearningContinuePage: React.FC = () => {
  const { user } = useAuthStore();
  const [learningSessions, setLearningSessions] = useState<LearningSession[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sample data - in production this would come from the API
  const sampleSessions: LearningSession[] = [
    {
      id: '1',
      courseTitle: 'Introduction to AI in Education',
      lessonTitle: 'Understanding Neural Networks',
      progress: 65,
      lastAccessed: '2 hours ago',
      estimatedTimeRemaining: '25 minutes',
      difficulty: 'Intermediate',
      category: 'AI & Technology',
      nextLessonTitle: 'Deep Learning Applications',
    },
    {
      id: '2',
      courseTitle: 'Personalized Learning Strategies',
      lessonTitle: 'Adaptive Assessment Techniques',
      progress: 80,
      lastAccessed: '1 day ago',
      estimatedTimeRemaining: '15 minutes',
      difficulty: 'Advanced',
      category: 'Teaching Methods',
      nextLessonTitle: 'Implementation Planning',
    },
    {
      id: '3',
      courseTitle: 'Family Engagement in Digital Learning',
      lessonTitle: 'Communication Tools and Strategies',
      progress: 30,
      lastAccessed: '3 days ago',
      estimatedTimeRemaining: '40 minutes',
      difficulty: 'Beginner',
      category: 'Family Education',
      nextLessonTitle: 'Digital Safety Guidelines',
    },
  ];

  const sampleActivity: RecentActivity[] = [
    {
      id: '1',
      type: 'lesson_completed',
      title: 'Machine Learning Basics',
      description: 'Completed lesson in AI Fundamentals course',
      timestamp: '2 hours ago',
      points: 50,
    },
    {
      id: '2',
      type: 'quiz_passed',
      title: 'Educational Psychology Quiz',
      description: 'Scored 85% on learning theory assessment',
      timestamp: '1 day ago',
      points: 75,
    },
    {
      id: '3',
      type: 'badge_earned',
      title: 'AI Pioneer Badge',
      description: 'Completed first AI education course',
      timestamp: '2 days ago',
      points: 100,
    },
    {
      id: '4',
      type: 'assignment_submitted',
      title: 'Learning Plan Design',
      description: 'Submitted personalized learning plan assignment',
      timestamp: '3 days ago',
    },
  ];

  useEffect(() => {
    const loadLearningData = async () => {
      try {
        setLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setLearningSessions(sampleSessions);
        setRecentActivity(sampleActivity);
      } catch (err) {
        setError('Failed to load learning data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadLearningData();
    }
  }, [user]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'green';
      case 'Intermediate':
        return 'cyan';
      case 'Advanced':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'lesson_completed':
        return <CheckmarkFilled size={16} />;
      case 'quiz_passed':
        return <CheckmarkFilled size={16} />;
      case 'badge_earned':
        return <CheckmarkFilled size={16} />;
      case 'assignment_submitted':
        return <CheckmarkFilled size={16} />;
      default:
        return <CheckmarkFilled size={16} />;
    }
  };

  const handleContinueLearning = (sessionId: string) => {
    // In production, this would navigate to the specific lesson
    alert(`Continuing learning session ${sessionId}`);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading your learning progress..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <Grid>
        <Column lg={16}>
          <div style={{ marginBottom: '2rem' }}>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 600, marginBottom: '0.5rem', color: '#0f62fe' }}>
              Continue Learning
            </h1>
            <p style={{ fontSize: '1.125rem', color: '#525252' }}>
              Pick up where you left off and continue your learning journey
            </p>
            {user && (
              <Tag type="blue" style={{ marginTop: '1rem' }}>
                Welcome back, {user.firstName || user.email}!
              </Tag>
            )}
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error loading learning data"
              subtitle={error}
              style={{ marginBottom: '2rem' }}
            />
          )}

          {/* Active Learning Sessions */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>Active Learning Sessions</h2>
            <Grid>
              {learningSessions.map(session => (
                <Column key={session.id} lg={8} md={8} sm={4} style={{ marginBottom: '1.5rem' }}>
                  <Tile style={{ height: '100%', padding: '1.5rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <Tag type={getDifficultyColor(session.difficulty)} size="sm">
                        {session.difficulty}
                      </Tag>
                      <Tag type="outline" size="sm" style={{ marginLeft: '0.5rem' }}>
                        {session.category}
                      </Tag>
                    </div>

                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      {session.courseTitle}
                    </h3>

                    <p style={{ fontSize: '1rem', color: '#525252', marginBottom: '1rem' }}>
                      Current: {session.lessonTitle}
                    </p>

                    <div style={{ marginBottom: '1rem' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Progress: {session.progress}%</span>
                        <span style={{ fontSize: '0.875rem', color: '#6f6f6f' }}>
                          {session.estimatedTimeRemaining} remaining
                        </span>
                      </div>
                      <ProgressBar
                        value={session.progress}
                        max={100}
                        status={session.progress === 100 ? 'finished' : 'active'}
                      />
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        fontSize: '0.875rem',
                        color: '#6f6f6f',
                      }}
                    >
                      <Time size={16} style={{ marginRight: '0.5rem' }} />
                      <span>Last accessed {session.lastAccessed}</span>
                    </div>

                    {session.nextLessonTitle && (
                      <p style={{ fontSize: '0.875rem', color: '#0f62fe', marginBottom: '1rem' }}>
                        Next: {session.nextLessonTitle}
                      </p>
                    )}

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Play}
                        onClick={() => handleContinueLearning(session.id)}
                      >
                        Continue
                      </Button>
                      <Button kind="ghost" size="sm" renderIcon={Bookmark} onClick={() => alert('Course bookmarked!')}>
                        Bookmark
                      </Button>
                    </div>
                  </Tile>
                </Column>
              ))}
            </Grid>

            {learningSessions.length === 0 && !loading && (
              <Tile style={{ textAlign: 'center', padding: '3rem' }}>
                <h3>No active learning sessions</h3>
                <p style={{ color: '#525252', marginBottom: '1.5rem' }}>
                  Start a new course to begin your learning journey!
                </p>
                <Button kind="primary" onClick={() => alert('Navigate to course catalog')}>
                  Browse Courses
                </Button>
              </Tile>
            )}
          </div>

          {/* Recent Activity */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>Recent Activity</h2>
            <Tile style={{ padding: '1.5rem' }}>
              <DataTable
                rows={recentActivity.map((activity, index) => ({
                  id: activity.id,
                  icon: getActivityIcon(activity.type),
                  activity: activity.title,
                  description: activity.description,
                  timestamp: activity.timestamp,
                  points: activity.points || 0,
                }))}
                headers={[
                  { key: 'icon', header: '' },
                  { key: 'activity', header: 'Activity' },
                  { key: 'description', header: 'Description' },
                  { key: 'timestamp', header: 'When' },
                  { key: 'points', header: 'Points' },
                ]}
              >
                {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <TableContainer>
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map(header => (
                            <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map(row => (
                          <TableRow {...getRowProps({ row })}>
                            {row.cells.map(cell => (
                              <TableCell key={cell.id}>
                                {cell.info.header === 'icon'
                                  ? cell.value
                                  : cell.info.header === 'points'
                                    ? cell.value > 0
                                      ? `+${cell.value}`
                                      : '-'
                                    : cell.value}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DataTable>

              {recentActivity.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: '#525252' }}>No recent activity to display</p>
                </div>
              )}
            </Tile>
          </div>
        </Column>
      </Grid>
    </div>
  );
};

export default LearningContinuePage;
